import { useTranslation } from 'react-i18next';
import { Wrench } from 'lucide-react';
import type { AssetFailureRow, CostlyLocationRow } from '@nbh/shared';
import { PeriodHeading } from './PeriodHeading';
import { formatMoneyMinor } from '../../lib/format';

/** §5.6 — equipment that keeps failing. */
export function RepeatFailuresPanel({
  rows,
  baselineAt,
}: {
  rows: AssetFailureRow[];
  baselineAt: string | null;
}) {
  const { t } = useTranslation();

  return (
    <section>
      <PeriodHeading label={t('admin.assets.heading')} baselineAt={baselineAt} />
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">{t('admin.assets.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {rows.map((asset) => (
            <li key={asset.assetId} className="flex items-center gap-3 p-3">
              <Wrench className="size-4 shrink-0 text-slate-500" aria-hidden="true" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{asset.assetName}</span>
                <span className="block truncate text-xs text-slate-500">
                  {asset.locationName ?? t('admin.assets.noLocation')}
                </span>
              </span>
              <span className="shrink-0 text-sm tabular-nums text-slate-300">
                {t('admin.assets.failures', { count: asset.failureCount })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** §5.6 — rooms consuming the most parts spend. */
export function CostlyRoomsPanel({
  rows,
  baselineAt,
}: {
  rows: CostlyLocationRow[];
  baselineAt: string | null;
}) {
  const { t } = useTranslation();

  return (
    <section>
      <PeriodHeading label={t('admin.rooms.heading')} baselineAt={baselineAt} />
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">{t('admin.rooms.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {rows.map((room) => (
            <li key={room.locationId} className="flex items-center gap-3 p-3">
              <span className="min-w-0 flex-1 truncate text-sm">{room.locationName}</span>
              <span className="shrink-0 text-sm tabular-nums text-slate-300">
                {formatMoneyMinor(room.partsCostMinor)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
